import { KnowledgeExtractionLog } from '@/types/knowledge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ChevronRight,
  ChevronLeft,
  ChevronsLeft,
  ChevronsRight,
  Download,
  Eye,
  Loader2,
  MoreHorizontal,
} from 'lucide-react';

interface LogsTableProps {
  logs: KnowledgeExtractionLog[];
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
  onRowClick: (id: string) => void;
  onExportCsv: (id: string) => void;
  exportingLogId?: string | null;
}

export function LogsTable({
  logs,
  page,
  totalPages,
  total,
  onPageChange,
  onRowClick,
  onExportCsv,
  exportingLogId,
}: LogsTableProps) {
  const hasPrevPage = page > 1;
  const hasNextPage = page < totalPages;

  const renderQuickActions = (logId: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={(event) => event.stopPropagation()}
          aria-label="Ações do log"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          onClick={(event) => {
            event.stopPropagation();
            onRowClick(logId);
          }}
        >
          <Eye className="h-4 w-4 mr-2" />
          Ver detalhes
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!!exportingLogId}
          onClick={(event) => {
            event.stopPropagation();
            onExportCsv(logId);
          }}
        >
          {exportingLogId === logId ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Exportar CSV
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div className="space-y-4">
      <div className="hidden md:block rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data/Hora</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Periodo</TableHead>
              <TableHead>Tickets</TableHead>
              <TableHead>Documentos</TableHead>
              <TableHead>Erros</TableHead>
              <TableHead>Duracao</TableHead>
              <TableHead className="w-[72px] text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.map((log) => (
              <TableRow
                key={log.id}
                className="cursor-pointer hover:bg-muted/30"
                onClick={() => onRowClick(log.id)}
              >
                <TableCell>
                  {format(new Date(log.runAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                </TableCell>
                <TableCell>
                  <Badge variant={log.runType === 'manual' ? 'default' : 'secondary'}>
                    {log.runType === 'manual' ? 'Manual' : 'Automatico'}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {format(new Date(log.dateRangeStart), 'dd/MM/yyyy', { locale: ptBR })}
                  {' - '}
                  {format(new Date(log.dateRangeEnd), 'dd/MM/yyyy', { locale: ptBR })}
                </TableCell>
                <TableCell>
                  {log.ticketsProcessed} / {log.ticketsFound}
                </TableCell>
                <TableCell>
                  {log.documentsCreated} criados, {log.documentsUpdated} atualizados
                </TableCell>
                <TableCell>
                  {log.errorsCount > 0 ? (
                    <Badge variant="destructive">{log.errorsCount}</Badge>
                  ) : (
                    <span className="text-muted-foreground">0</span>
                  )}
                </TableCell>
                <TableCell>{(log.durationMs / 1000).toFixed(2)}s</TableCell>
                <TableCell className="text-right">
                  {renderQuickActions(log.id)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="md:hidden space-y-3">
        {logs.map((log) => (
          <Card
            key={log.id}
            className="cursor-pointer p-4 hover:bg-muted/30"
            onClick={() => onRowClick(log.id)}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="space-y-2">
                <div className="text-sm font-medium">
                  {format(new Date(log.runAt), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                </div>
                <Badge variant={log.runType === 'manual' ? 'default' : 'secondary'}>
                  {log.runType === 'manual' ? 'Manual' : 'Automatico'}
                </Badge>
                <div className="text-xs text-muted-foreground">
                  {log.ticketsProcessed} / {log.ticketsFound} tickets
                </div>
                <div className="text-xs text-muted-foreground">
                  {log.documentsCreated} criados, {log.documentsUpdated} atualizados
                </div>
              </div>
              <div className="flex items-center gap-1">
                {renderQuickActions(log.id)}
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-2">
        <p className="text-sm text-muted-foreground">
          Total de {total.toLocaleString()} logs
        </p>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(1)}
            disabled={!hasPrevPage}
            className="h-8 w-8 p-0"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={!hasPrevPage}
            className="h-8 w-8 p-0"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1 text-sm">
            <span className="font-medium">{page}</span>
            <span className="text-muted-foreground">de</span>
            <span className="font-medium">{totalPages || 1}</span>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={!hasNextPage}
            className="h-8 w-8 p-0"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(totalPages || 1)}
            disabled={!hasNextPage}
            className="h-8 w-8 p-0"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
